import {
  formatFfsScalar,
  WORKING_UNIT_THICKNESS,
} from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { GeckoMeasurementUnit } from 'types'

import {
  ClassificationFacts,
  ComponentType,
  RegionKind,
  ShellGeometry,
} from './ffsComponentClassification'
import { CorrosionType, EntryRoute } from './ffsRegionClassificationSchema'
import { CorrosionBasis } from './ffsRegionRouting'
import { PipingMaterialGroup } from './pipingMaterialGroups'

// The describe* helpers below receive canonical millimetre scalars and format
// them for display via `formatFfsScalar` (single source of FFS precision).
// `displayUnit` follows the caller's global measurement system; comparisons stay
// in mm (see each function). Values are unrounded before conversion, so no
// precision is lost from repeated edits upstream.
const mmToDisplay = (mm: number, unit: GeckoMeasurementUnit): string =>
  formatFfsScalar(mm, WORKING_UNIT_THICKNESS, unit)

/**
 * Cited, plain-language help content for the FFS pre-planning classification.
 *
 * Every entry pairs a `summary` (our explanation of what a choice means and why
 * it matters) with a `citation` back to API 579-1/ASME FFS-1 (2021). This is
 * CONTENT — deliberately separate from the classification rules
 * (`ffsComponentClassification.ts`) and from the React layer — so wording can
 * change without touching logic. The `citation` is never empty (enforced by a
 * test): everything shown to the engineer is traceable to the standard.
 *
 * These are faithful summaries of the standard's intent, NOT verbatim quotes.
 */
export interface CitedNote {
  summary: string
  citation: string
}

/**
 * A selectable option: its display label AND its cited explanation, in ONE entry.
 * Keeping them together is deliberate — they used to live in two parallel records
 * in two files, which meant an option could gain a label without gaining help.
 */
export interface OptionMeta extends CitedNote {
  label: string
}

/**
 * The `{ value, label }` list for a select, derived from an option-meta record so
 * the labels exist exactly once. Declaration order of the record is the display
 * order.
 *
 * Returns a fresh mutable array (antd's `Select` requires one) — it is built here
 * and never shared, so handing it out mutable is accurate rather than a leak.
 */
export const optionsOf = <K extends string>(
  meta: Record<K, OptionMeta>
): Array<{ value: K; label: string }> =>
  (Object.keys(meta) as K[]).map((value) => ({
    value,
    label: meta[value].label,
  }))

// --- Per-question help (keyed by `ClassificationFacts` fact id) -------------

/**
 * Keyed by the fact union (not `string`), so every fact is guaranteed a cited
 * note at compile time and consumers need no presence guard.
 */
export const FACT_HELP: Record<keyof ClassificationFacts, CitedNote> = {
  region: {
    summary:
      'Where the metal loss sits determines the API 579 component type ' +
      '(A / B1 / B2 / C), which in turn fixes which FFS Parts (4 / 5) and ' +
      'Levels (1 / 2) may be used. A shell region remote from structural ' +
      'discontinuities is the least restricted; regions at nozzles, ' +
      'junctions, or supports are more restricted because of the stress ' +
      'concentrations there. Only shell-body regions are offered for now: the ' +
      'inspection data visualization does not yet place indications anywhere ' +
      'else. The other region types remain implemented and correctly ' +
      'classified, so a previously saved one still works.',
    citation: 'API 579-1 Part 4, §4.2.5 & Table 4.2 (component type).',
  },
  headIs2to1Elliptical: {
    summary:
      'A 2:1 elliptical head has a code design equation, so the exception ' +
      'that lets a head-to-shell junction be treated as a (Type A) shell ' +
      'region can only apply to it — other head shapes at the junction stay ' +
      'Type C.',
    citation: 'API 579-1 Part 4, §4.2.5 / Table 4.2 (Type A exception).',
  },
  onCylinderSide: {
    summary:
      'The Type A exception applies only on the cylinder (shell) side of the ' +
      'head-to-shell junction, where the shell design equation governs — not ' +
      'on the head/knuckle side.',
    citation: 'API 579-1 Part 4, §4.2.5 / Table 4.2.',
  },
  pressureOnlyLoading: {
    summary:
      'The exception assumes internal pressure is the governing load. If ' +
      'supplemental loads (weight, thermal, nozzle loads) also govern, the ' +
      'junction is not eligible for Type A and remains Type C.',
    citation: 'API 579-1 Part 4, §4.2.5 / Table 4.2.',
  },
  stiffenerRatio: {
    summary:
      'For a stiffening-ring attachment, the Fig 4.18 attachment-ratio ' +
      'decides the type: above 0.38 the local stresses are high enough that ' +
      'the region is Type C; at or below 0.38 it is assessed with the shell ' +
      'rules.',
    citation: 'API 579-1 Part 4, Fig 4.18.',
  },
  shellGeometry: {
    summary:
      'The shell geometry selects which Type A dimensional/loading limits ' +
      'apply: cylindrical shells use the horizontal (Lss/D) or vertical ' +
      '(H/D) limits, piping uses the Fig 4.5 size/temperature bands, and a ' +
      'sphere or other code-designed body is checked for a code design ' +
      'equation.',
    citation: 'API 579-1 Part 4, §4.2.5, Fig 4.3 / 4.4 / 4.5.',
  },
  Lss: {
    summary:
      'The shell section length between stiffening lines. A horizontal ' +
      'cylindrical shell qualifies as Type A only when Lss/D ≤ 2.5 and D is ' +
      'within the diameter limit; longer spans concentrate stress and drop to ' +
      'Type B1.',
    citation: 'API 579-1 Part 4, Fig 4.3 (Type A dimensional limits).',
  },
  H: {
    summary:
      'The height of a vertical cylindrical shell. It qualifies as Type A ' +
      'only when H/D ≤ 3.0 and H is within the height limit; taller shells ' +
      'drop to Type B1.',
    citation: 'API 579-1 Part 4, Fig 4.4 (Type A dimensional limits).',
  },
  D: {
    summary:
      'The shell diameter used in the Type A dimensional limits (Lss/D, H/D, ' +
      'and the absolute diameter caps). Note: this is prefilled from the ' +
      'component outer diameter and may need conversion to the inside ' +
      'diameter the figures use.',
    citation: 'API 579-1 Part 4, Fig 4.3 / 4.4.',
  },
  materialGroup: {
    summary:
      'The Fig 4.5 piping criteria differ by material family — carbon & low ' +
      'alloy steels get a wider temperature envelope than high alloy & ' +
      'nonferrous steels — so the material group selects which band table ' +
      'applies.',
    citation: 'API 579-1 Part 4, Fig 4.5 / 4.5M.',
  },
  nps: {
    summary:
      'Nominal pipe size (inches). Type A eligibility for piping is bounded ' +
      'by NPS: the shaded Fig 4.5 region only extends to NPS 16 (carbon) or ' +
      'NPS 10 (high alloy); larger sizes fall to Type B1.',
    citation: 'API 579-1 Part 4, Fig 4.5.',
  },
  designTempC: {
    summary:
      'Design temperature for the region, entered in your selected units. Type ' +
      'A piping is limited to a temperature window that narrows as pipe size ' +
      'grows; outside the Fig 4.5 window the region is Type B1.',
    citation: 'API 579-1 Part 4, Fig 4.5 / 4.5M.',
  },
  pressureGovernsThickness: {
    summary:
      'Type A requires that a code equation relating internal pressure (or ' +
      'fill) to required thickness governs the region. If pressure does not ' +
      'govern the required thickness, the region is Type B1. This is NO LONGER ' +
      'ASKED — it is assumed to hold, because FFS is not run here on ' +
      'components where pressure does not govern. Consequence of that ' +
      'assumption: a region whose thickness is set by minimum structural ' +
      'thickness, with no governing supplemental loads, is classified Type A ' +
      'rather than Type B1. An answer recorded before the question was ' +
      'withdrawn is still honoured.',
    citation: 'API 579-1 Part 4, §4.2.5.',
  },
  // NO LONGER ASKED — assumed false. Answering it correctly would require a
  // chain of calculations not relevant at this stage, and the fleet assumption
  // is that supplemental loads do not govern. Consequence: a region whose
  // supplemental loads actually do govern is classified Type A rather than
  // Type B1. An answer recorded before the question was withdrawn is still
  // honoured. Kept here only because FACT_HELP is an exhaustive
  // Record<keyof ClassificationFacts>.
  supplementalLoadsGovern: {
    summary:
      'Even when pressure governs, if supplemental loads (weight, wind, ' +
      'seismic, thermal, nozzle loads) govern the design, the region is not ' +
      'Type A and drops to Type B1.',
    citation: 'API 579-1 Part 4, §4.2.5.',
  },
  hasCodeDesignEquation: {
    summary:
      'Type A hinges on the existence of a closed-form construction-code ' +
      'equation relating pressure to required thickness for the region. ' +
      'Without one, the region is classified by the generic fallback ' +
      '(Type B2 if a code assessment procedure exists, else Type C).',
    citation: 'API 579-1 Part 4, §4.2.5.',
  },
  codeAssessmentProcedureAvailable: {
    summary:
      'When no design equation exists, a region may still be Type B2 if the ' +
      'construction code provides an assessment procedure for it; if neither ' +
      'exists it is Type C and outside the Level 1/2 FFS scope.',
    citation: 'API 579-1 Part 4, §4.2.5.',
  },
}

// --- Selectable options for the enum facts (label + cited help) --------------

// Total (not `Partial`) on purpose: every option the engineer can pick must come
// with a label AND a cited explanation, and a new RegionKind without one should
// not compile. Declaration order is the order the select renders.
export const REGION_META: Record<RegionKind, OptionMeta> = {
  'remote-shell-body': {
    // Labelled "Shell body", not "Remote shell body": remoteness from
    // structural discontinuities is a claim the automatic check is not able to
    // make (see `REMOTENESS_INFO`), so the label should not assert it. The
    // persisted VALUE stays `remote-shell-body` — renaming it would break every
    // saved record.
    label: 'Shell body',
    summary:
      'A shell/head region, assessed on the assumption that it is remote from ' +
      'structural discontinuities — a candidate for Type A (or B1 if it fails ' +
      'the dimensional/loading limits). Nearby features raise a warning below, ' +
      'but that check cannot confirm remoteness on its own.',
    citation: 'API 579-1 Part 4, §4.2.5 / Table 4.2.',
  },
  nozzle: {
    label: 'Nozzle',
    summary:
      'A nozzle or opening reinforcement is a listed Type B2 component ' +
      '(structural discontinuity): Part 4 Level 2 only, no Part 5.',
    citation: 'API 579-1 Part 4, §4.2.5 (Table 4.2, B2 list).',
  },
  branch: {
    label: 'Branch connection',
    summary:
      'A branch connection is a listed Type B2 structural discontinuity: ' +
      'Part 4 Level 2 only, no Part 5.',
    citation: 'API 579-1 Part 4, §4.2.5 (Table 4.2, B2 list).',
  },
  flange: {
    label: 'Flange',
    summary: 'A flange is a listed Type B2 component: Part 4 Level 2 only.',
    citation: 'API 579-1 Part 4, §4.2.5 (Table 4.2, B2 list).',
  },
  tubesheet: {
    label: 'Tubesheet',
    summary: 'A tubesheet is a listed Type B2 component: Part 4 Level 2 only.',
    citation: 'API 579-1 Part 4, §4.2.5 (Table 4.2, B2 list).',
  },
  'cone-reinforcement': {
    label: 'Cone reinforcement zone',
    summary:
      'A cone (conical transition) reinforcement zone is a listed Type B2 ' +
      'structural discontinuity: Part 4 Level 2 only.',
    citation: 'API 579-1 Part 4, §4.2.5 (Table 4.2, B2 list).',
  },
  'cyl-to-flat-head-jct': {
    label: 'Cylinder-to-flat-head junction',
    summary:
      'A cylinder-to-flat-head junction is a listed Type B2 discontinuity: ' +
      'Part 4 Level 2 only.',
    citation: 'API 579-1 Part 4, §4.2.5 (Table 4.2, B2 list).',
  },
  'head-to-shell-jct': {
    label: 'Head-to-shell junction',
    summary:
      'A head-to-shell junction is Type C by default (major structural ' +
      'discontinuity, Level 3), unless the 2:1-elliptical cylinder-side ' +
      'pressure-only exception applies (then Type A).',
    citation: 'API 579-1 Part 4, §4.2.5 / Table 4.2.',
  },
  skirt: {
    label: 'Skirt attachment',
    summary:
      'A skirt attachment is a listed Type C region (major structural ' +
      'discontinuity): not eligible for Part 4/5 Level 1 or 2 — Level 3.',
    citation: 'API 579-1 Part 4, §4.2.5 (Table 4.2, C list).',
  },
  lug: {
    label: 'Lug attachment',
    summary:
      'A lug attachment is a listed Type C region: Level 3 assessment ' +
      'required, outside the Level 1/2 scope here.',
    citation: 'API 579-1 Part 4, §4.2.5 (Table 4.2, C list).',
  },
  'support-attach': {
    label: 'Support attachment',
    summary:
      'A support attachment is a listed Type C region: Level 3 assessment ' +
      'required.',
    citation: 'API 579-1 Part 4, §4.2.5 (Table 4.2, C list).',
  },
  'tank-shell-to-bottom-jct': {
    label: 'Tank shell-to-bottom junction',
    summary:
      'A tank shell-to-bottom junction is a listed Type C region: Level 3 ' +
      'assessment required.',
    citation: 'API 579-1 Part 4, §4.2.5 (Table 4.2, C list).',
  },
  'stiffening-ring-attach': {
    label: 'Stiffening-ring attachment',
    summary:
      'A stiffening-ring attachment is judged by the Fig 4.18 attachment ' +
      'ratio: > 0.38 → Type C, otherwise assessed with the shell rules.',
    citation: 'API 579-1 Part 4, Fig 4.18.',
  },
  other: {
    label: 'Other / unlisted region',
    summary:
      'An unlisted region is classified by the generic fallback: a code ' +
      'design equation + pressure governing → Type A/B1; otherwise Type B2 ' +
      '(procedure available) or Type C.',
    citation: 'API 579-1 Part 4, §4.2.5.',
  },
}

export const SHELL_GEOMETRY_META: Record<ShellGeometry, OptionMeta> = {
  horizontal: {
    label: 'Horizontal cylindrical shell',
    summary:
      'Horizontal cylindrical shell — Type A requires Lss/D ≤ 2.5 and ' +
      'D ≤ 3.05 m (10 ft); otherwise Type B1.',
    citation: 'API 579-1 Part 4, Fig 4.3.',
  },
  vertical: {
    label: 'Vertical cylindrical shell',
    summary:
      'Vertical cylindrical shell — Type A requires H/D ≤ 3.0 and ' +
      'H ≤ 30.5 m (100 ft); otherwise Type B1.',
    citation: 'API 579-1 Part 4, Fig 4.4.',
  },
  sphere: {
    label: 'Spherical shell / head',
    summary:
      'Spherical shell or hemispherical head — Type A only if a code design ' +
      'equation applies and pressure governs; a geometric sphere is not ' +
      'automatically Type A.',
    citation: 'API 579-1 Part 4, §4.2.5.',
  },
  piping: {
    label: 'Piping',
    summary:
      'Straight pipe, elbow, or bend with no structural attachment — Type A ' +
      'if the size and design temperature fall within the Fig 4.5 band for ' +
      'the material group; otherwise Type B1.',
    citation: 'API 579-1 Part 4, Fig 4.5 / 4.5M.',
  },
  'other-code-designed-body': {
    label: 'Other code-designed body',
    summary:
      'A code-designed body without a cylinder/sphere/piping mapping (cone, ' +
      'torispherical/ellipsoidal head) — classified by the generic fallback.',
    citation: 'API 579-1 Part 4, §4.2.5.',
  },
}

export const MATERIAL_GROUP_META: Record<PipingMaterialGroup, OptionMeta> = {
  'carbon-low-alloy': {
    label: 'Carbon & Low Alloy Steels',
    summary:
      'Carbon & low alloy steels — the wider Fig 4.5(a) Type A envelope: ' +
      'floor −46 °C, up to NPS 16, ceiling stepping 204 → 149 → 93 °C as ' +
      'size grows.',
    citation: 'API 579-1 Part 4, Fig 4.5(a) / 4.5M(a).',
  },
  'high-alloy-nonferrous': {
    label: 'High Alloy & Nonferrous Steels',
    summary:
      'High alloy & nonferrous steels — the narrower Fig 4.5(b) Type A ' +
      'envelope: ceiling 93 °C, up to NPS 10, floor −101 °C (NPS ≤ 4) or ' +
      '−46 °C (larger).',
    citation: 'API 579-1 Part 4, Fig 4.5(b) / 4.5M(b).',
  },
}

// --- Component-type definitions ---------------------------------------------

export const COMPONENT_TYPE_INFO: Record<
  ComponentType,
  CitedNote & { title: string }
> = {
  A: {
    title: 'Type A — code-designed region, remote from discontinuities',
    summary:
      'A region with a closed-form construction-code equation relating ' +
      'pressure to required thickness, remote from structural ' +
      'discontinuities. The least restricted type — all Parts and Levels ' +
      '(Part 4 L1/L2, Part 5 L1/L2) are available.',
    citation: 'API 579-1 Part 4, §4.2.5 / Table 4.2.',
  },
  B1: {
    title: 'Type B, Class 1 — code-designed but limited',
    summary:
      'A component with a code design equation that fails a Type A ' +
      'dimensional or loading limit. Assessed at Part 4 Level 2 or Part 5 ' +
      'Level 2 (no Level 1).',
    citation: 'API 579-1 Part 4, §4.2.5, §4.4.3.3; Part 5.',
  },
  B2: {
    title: 'Type B, Class 2 — structural discontinuity',
    summary:
      'A listed structural discontinuity (nozzle, flange, tubesheet, ' +
      'cone reinforcement, cylinder-to-flat-head junction). Part 4 Level 2 ' +
      'only — NOT eligible for Part 5 (Level 3 required for local metal loss ' +
      'there).',
    citation: 'API 579-1 Part 4, §4.2.5; Part 5 §5.2.5(d) & §5.2.6.',
  },
  C: {
    title: 'Type C — major structural discontinuity',
    summary:
      'A major structural discontinuity (head-to-shell/skirt/lug/support ' +
      'junction, tank shell-to-bottom). Not eligible for Part 4 or Part 5 ' +
      'Level 1/2 — a Level 3 assessment is required, which is outside this ' +
      "step's scope.",
    citation: 'API 579-1 Part 4, §4.2.5 / Table 4.2.',
  },
}

// --- Corrosion type (Rule 1) — derived readout ------------------------------

export const CORROSION_INFO: {
  rule: CitedNote
  general: CitedNote
  local: CitedNote
  groove: CitedNote
} = {
  rule: {
    summary:
      'Corrosion type is decided by uniformity FIRST: the minimum measured ' +
      'thickness must be within 10% of the average (t_mm ≥ 0.9·t_am), or the ' +
      'loss is local. A uniform region is general OUTRIGHT — its footprint ' +
      "does not matter. Only WITHIN local metal loss does the region's " +
      'footprint matter: a length-to-width ratio beyond 10:1 is a groove-like ' +
      'flaw, assessed by a separate API 579 procedure that is OUT OF SCOPE ' +
      'here — it is not routed to Part 5. Ordinary local metal loss requires ' +
      'thickness profiles.',
    citation: 'API 579-1 Part 4, §4.3.3.2, Eq. 4.1; Part 5, §5.2.1.',
  },
  general: {
    summary:
      'General metal loss — relatively uniform wall loss; Part 4 (general ' +
      'metal loss) is the primary assessment method. Uniformity alone decides ' +
      'this — the footprint is never checked.',
    citation: 'API 579-1 Part 4, §4.3.3.',
  },
  local: {
    summary:
      'Local metal loss — thickness profiles / critical thickness profiles ' +
      'are required, and Part 5 (local metal loss) is the primary method. ' +
      'Covers a compact local thin area; an elongated (groove-like) footprint ' +
      'is a separate, out-of-scope disposition instead.',
    citation: 'API 579-1 Part 5, §5.2.1, §5.3.3.',
  },
  groove: {
    summary:
      'A local thin area has a length of the same order of magnitude as its ' +
      'width; a groove-like flaw is an elongated thin spot from directional ' +
      'erosion or corrosion, whose length is much greater than its width. A ' +
      'groove is NOT assessed by this tool — API 579 Part 5 gives it a ' +
      'separate procedure this workflow does not implement, so it is flagged ' +
      'and left out of scope rather than routed anywhere. The standard gives ' +
      'no numeric limit — we treat a length-to-width ratio beyond 10:1 as ' +
      'groove-like, reading "same order of magnitude" as a factor of ten.',
    citation: 'API 579-1 Part 5, §5.2.1(a) and §5.2.1(b)(1).',
  },
}

const CORROSION_BY_TYPE: Record<CorrosionType, CitedNote> = {
  general: CORROSION_INFO.general,
  local: CORROSION_INFO.local,
  'groove-like': CORROSION_INFO.groove,
}

/** The cited note for a resolved corrosion type. */
export function corrosionNote(type: CorrosionType): CitedNote {
  return CORROSION_BY_TYPE[type]
}

// --- Plain-language labels for the result readout ---------------------------

/**
 * Human-readable label for the executed entry route. The raw enum
 * (`PART_5_LEVEL_1`, …) must never surface in the UI — this maps it to the
 * phrasing shown in the pre-planning result.
 */
export const ENTRY_ROUTE_LABELS: Record<EntryRoute, string> = {
  PART_4_LEVEL_1: 'Part 4, Level 1 assessment',
  PART_4_LEVEL_2: 'Part 4, Level 2 assessment',
  PART_5_LEVEL_1: 'Part 5, Level 1 assessment',
  PART_5_LEVEL_2: 'Part 5, Level 2 assessment',
  NEITHER_APPLICABLE: 'No Level 1/2 route — a Level 3 assessment is required',
  GROOVE_NOT_COVERED:
    'Groove-like flaw — a separate Part 5 groove procedure applies, outside this tool',
}

// --- Discontinuity proximity — a warning, never an affirmation ---------------

export const REMOTENESS_INFO: CitedNote = {
  summary:
    'Type A requires the region to be remote from major structural ' +
    'discontinuities: its distance to the nearest one must clear ' +
    '1.8·√(D·t_c). The rulers generated with the LTAs measure distance to ' +
    'nearby asset-model features, so we use them to WARN when something is ' +
    'close — never to confirm that a region is remote. Two blind spots make ' +
    'that one-way: the rulers carry no feature type, so welds and plate ' +
    'boundaries count the same as a nozzle and can raise a warning that does ' +
    'not matter; and features the LTA generator does not model — head-to-shell ' +
    'junctions among them — produce no ruler at all, so silence here is not ' +
    'evidence that nothing is nearby. t_c is also approximated by the nominal ' +
    'wall, since the future corrosion allowance is not captured at this step.',
  citation: 'API 579-1 Part 5, Eq. 5.10; Part 4, §4.2.5.',
}

/**
 * Warn when a modelled feature sits inside the Eq. 5.10 limit.
 *
 * Returns `null` when the check passes AND when there are no measurements at
 * all — silence is the correct output in both cases. This function deliberately
 * has no "this region is remote" branch: the rulers cannot establish that (see
 * the blind spots in `REMOTENESS_INFO`), so the only claim it is entitled to
 * make is the negative one. The region default comes from the inspection data,
 * not from here.
 */
export function describeDiscontinuityProximity(
  {
    lmsdMm,
    nearestFeatureTitle,
    thresholdMm,
  }: {
    lmsdMm: number | null
    nearestFeatureTitle: string | null
    thresholdMm: number | null
  },
  displayUnit: GeckoMeasurementUnit = WORKING_UNIT_THICKNESS
): { title: string; detail: string } | null {
  // Comparison stays in canonical mm; only the interpolated values are converted.
  if (lmsdMm === null || thresholdMm === null || lmsdMm >= thresholdMm) {
    return null
  }

  const feature = nearestFeatureTitle
    ? `Nearest modelled feature ${nearestFeatureTitle} is`
    : 'The nearest modelled feature is'
  const u = displayUnit.name

  return {
    title: 'This region may not be remote from a structural discontinuity.',
    detail:
      `${feature} ${mmToDisplay(lmsdMm, displayUnit)} ${u} away, inside the ` +
      `${mmToDisplay(thresholdMm, displayUnit)} ${u} limit for this geometry (1.8·√(D·t_c)). ` +
      'The check counts every modelled feature, including welds and plate ' +
      'boundaries, so it may not be a major structural discontinuity. Only ' +
      'shell-body regions are supported here — if one is genuinely nearby, ' +
      'this LTA needs assessing outside this step.',
  }
}

/** Plain-language label for the derived/selected corrosion type. */
export const CORROSION_TYPE_LABELS: Record<CorrosionType, string> = {
  general: 'General (uniform) metal loss',
  local: 'Local (non-uniform) metal loss',
  'groove-like': 'Groove-like flaw (not covered)',
}

/**
 * The corrosion-type readout shown under the selector. Split into `label` (which
 * the view bolds) and `detail` rather than one string, so all the WORDING lives
 * here while the markup stays the view's business.
 */
export type DerivedCorrosionReadout =
  | { kind: 'undetermined'; message: string }
  | { kind: 'derived'; label: string; detail: string }

/**
 * Describe what was derived from the LTA's thickness stats: which corrosion type,
 * what that means for the assessment, the numbers behind it, and whether the
 * engineer has overridden it.
 *
 * A pure function so this prose is unit-tested instead of assembled from nested
 * ternaries inside JSX. Thicknesses are in mm (the working unit) and may be
 * `null` when the LTA has no usable stats.
 */
export function describeDerivedCorrosion(
  {
    basis,
    tMmMm,
    tAmMm,
    lengthMm,
    widthMm,
    overridden,
  }: {
    basis: CorrosionBasis
    tMmMm: number | null
    tAmMm: number | null
    lengthMm: number | null
    widthMm: number | null
    overridden: boolean
  },
  displayUnit: GeckoMeasurementUnit = WORKING_UNIT_THICKNESS
): DerivedCorrosionReadout {
  // The gate that decided is HANDED to us by `classifyCorrosionType`. Do not
  // re-run the uniformity test here to work it out backwards — that would put
  // Rule 1 in two modules and let the verdict and its explanation drift apart.
  if (basis === 'no-thickness') {
    return {
      kind: 'undetermined',
      message:
        'No thickness statistics for this LTA — set the corrosion type manually.',
    }
  }

  const u = displayUnit.name
  const thickness =
    tMmMm !== null && tAmMm !== null
      ? {
          min: mmToDisplay(tMmMm, displayUnit),
          avg: mmToDisplay(tAmMm, displayUnit),
        }
      : null
  // Aspect ratio is unit-invariant, so it is computed from the canonical mm.
  const footprint =
    lengthMm !== null && widthMm !== null && widthMm > 0
      ? `${mmToDisplay(lengthMm, displayUnit)} × ${mmToDisplay(widthMm, displayUnit)} ${u} footprint is a ${(lengthMm / widthMm).toFixed(1)}:1 aspect ratio`
      : null

  switch (basis) {
    // Gate 1 passed: general outright, whatever the footprint — the footprint
    // is never even consulted, so this readout never mentions an aspect ratio.
    case 'uniform':
      return readout('general', overridden, [
        ' — point-reading thickness data is valid.',
        thickness &&
          ` Minimum wall ${thickness.min} ${u} is within 10% of the ${thickness.avg} ${u} average.`,
      ])
    // Gate 1 failed, and the footprint is compact (or unknown) — ordinary local
    // metal loss.
    case 'non-uniform':
      return readout('local', overridden, [
        ' — thickness profiles are required.',
        thickness &&
          ` Minimum wall ${thickness.min} ${u} falls below 90% of the ${thickness.avg} ${u} average.`,
      ])
    // Gate 1 failed AND the footprint is elongated — a separate, out-of-scope
    // procedure, not a Part 5 local-metal-loss route.
    case 'groove-like':
      return readout('groove-like', overridden, [
        ' — not assessed by this tool; a separate API 579 procedure applies.',
        thickness &&
          ` Minimum wall ${thickness.min} ${u} falls below 90% of the ${thickness.avg} ${u} average${footprint ? `, and the ${footprint}` : ''}.`,
      ])
  }
}

/** Assemble a derived readout, dropping any sentence whose figures were absent. */
function readout(
  corrosionType: CorrosionType,
  overridden: boolean,
  parts: ReadonlyArray<string | null>
): DerivedCorrosionReadout {
  return {
    kind: 'derived',
    label: CORROSION_TYPE_LABELS[corrosionType],
    detail: `${parts.filter(Boolean).join('')}${overridden ? ' (manually overridden)' : ''}`,
  }
}
